import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
    DEFAULT_WOOD_PROVINCE_KEY,
    FRAMING_GROUP_EMB_C_KEY,
    getFramingEmbodiedCarbonDisplay,
    getRawMapFramingCarbon,
    getWoodFramingDefaultSourceLabel,
    isWoodFramingForEmbodiedCarbon,
    scaleFramingByWoodProvinceKey,
    WOOD_AVERAGE_REGION_KEYS,
} from "utils/enclosure/embodiedCarbon";
import {
    parseEmbodiedNum,
    useEmbodiedFieldId,
    useExpandableGradeCellHandlers,
} from "./embodiedFormUtils";

import GradeDisplay from "./GradeDisplay";
import PercentageDisplay from "./PercentageDisplay";
import { sanitizeSourceInput, USER_DEFINED_LABEL } from "./wallLayerEmbodiedCarbon";

import classes from "./style.module.scss";

const formatDraftNum = (n) => {
    if (n == null || Number.isNaN(n)) return "";
    return String(Math.round(n * 1e6) / 1e6);
};

/**
 * Framing / structure embodied carbon grade for standard codes (structure, spacing, type/size, studs).
 * Optional `change` + `layersFieldPrefix` enable user-defined `framing_embC` (wall prototype).
 * Wood-framed walls: optional provincial wood-average scaling (see WOOD_AVERAGES), stored on `framing_embC.woodProvinceCode`.
 */
const EmbodiedCarbonFramingGroupGrade = React.memo(
    ({
        layers,
        componentType = "wall",
        customPadding = "0 7px",
        customGap = "0.75rem",
        borderHeight = "100%",
        change,
        layersFieldPrefix,
        barMetric = "percentage",
    }) => {
        const [expanded, setExpanded] = useState(false);
        const [draftEmissions, setDraftEmissions] = useState("");
        const [draftStorage, setDraftStorage] = useState("");
        const [draftSource, setDraftSource] = useState("");
        const [draftWoodProvince, setDraftWoodProvince] = useState(DEFAULT_WOOD_PROVINCE_KEY);

        const sourceInputId = useEmbodiedFieldId("volta-emb-framing-source");
        const expandCellHandlers = useExpandableGradeCellHandlers(setExpanded);

        const showEditor = typeof change === "function" && typeof layersFieldPrefix === "string";
        const embPath = `${layersFieldPrefix}.${FRAMING_GROUP_EMB_C_KEY}`;
        const embC = layers?.[FRAMING_GROUP_EMB_C_KEY];

        const isWood = useMemo(() => isWoodFramingForEmbodiedCarbon(layers, componentType), [layers, componentType]);

        const display = useMemo(
            () => getFramingEmbodiedCarbonDisplay({ layers, componentType }),
            [layers, componentType]
        );

        const applyWoodProvinceToDrafts = useCallback(
            (provinceKey) => {
                const raw = getRawMapFramingCarbon(layers, componentType);
                const scaled = scaleFramingByWoodProvinceKey(raw.emissions, raw.storage, provinceKey);
                setDraftEmissions(formatDraftNum(scaled.emissions));
                setDraftStorage(formatDraftNum(scaled.storage));
            },
            [layers, componentType]
        );

        const onWoodProvinceChange = useCallback(
            (e) => {
                const key = e.target.value;
                setDraftWoodProvince(key);
                applyWoodProvinceToDrafts(key);
                setDraftSource(sanitizeSourceInput(getWoodFramingDefaultSourceLabel(key)));
            },
            [applyWoodProvinceToDrafts]
        );

        /* Hydrate drafts from saved framing_embC (authoritative until cleared). */
        useEffect(() => {
            if (!expanded || !showEditor) return;
            if (!embC || embC.emissions == null) return;

            setDraftEmissions(String(embC.emissions));
            setDraftStorage(embC.storage != null ? String(embC.storage) : "");
            const prov = embC.woodProvinceCode ?? DEFAULT_WOOD_PROVINCE_KEY;
            setDraftWoodProvince(prov);
            const savedSrc = embC.source != null && embC.source !== "" ? embC.source : null;
            setDraftSource(
                sanitizeSourceInput(
                    savedSrc != null ? savedSrc : isWood ? getWoodFramingDefaultSourceLabel(prov) : USER_DEFINED_LABEL
                )
            );
        }, [expanded, showEditor, embC, isWood]);

        /*
         * Map defaults × provincial wood ratio when no custom framing_embC, or after reset.
         * Keeps selected wood region when spacing/type changes (no saved emb).
         */
        useEffect(() => {
            if (!expanded || !showEditor) return;
            if (embC && embC.emissions != null) return;

            const raw = getRawMapFramingCarbon(layers, componentType);
            if (!isWoodFramingForEmbodiedCarbon(layers, componentType)) {
                setDraftEmissions(raw.emissions != null ? String(raw.emissions) : "");
                setDraftStorage(raw.storage != null ? String(raw.storage) : "");
                return;
            }
            applyWoodProvinceToDrafts(draftWoodProvince);
            setDraftSource(sanitizeSourceInput(getWoodFramingDefaultSourceLabel(draftWoodProvince)));
        }, [expanded, showEditor, embC, layers, componentType, draftWoodProvince, applyWoodProvinceToDrafts]);

        const applyEmbC = useCallback(() => {
            if (!change) return;
            const e = parseEmbodiedNum(draftEmissions);
            if (e == null) return;

            const s = parseEmbodiedNum(draftStorage);
            const trimmed = sanitizeSourceInput(draftSource).trim();
            const nextSource =
                trimmed === ""
                    ? isWood
                        ? sanitizeSourceInput(getWoodFramingDefaultSourceLabel(draftWoodProvince)).trim() ||
                          USER_DEFINED_LABEL
                        : USER_DEFINED_LABEL
                    : trimmed;

            const payload = {
                emissions: e,
                storage: s ?? 0,
                source: nextSource,
                structureCodeValue: layers?.structureType?.value,
                componentTypeSizeValue: layers?.componentTypeSize?.value,
                spacingValue: layers?.spacing?.value,
                framingValue: layers?.framing?.value,
            };

            if (isWood) {
                payload.woodProvinceCode = draftWoodProvince || DEFAULT_WOOD_PROVINCE_KEY;
            }

            change(embPath, payload);
        }, [change, embPath, draftEmissions, draftStorage, draftSource, draftWoodProvince, layers, isWood]);

        const resetEmbC = useCallback(() => {
            if (change) change(embPath, null);
            setExpanded(false);
        }, [change, embPath]);

        useEffect(() => {
            if (!showEditor) setExpanded(false);
        }, [showEditor]);

        const showGrade = barMetric === "grade";

        const gradeInner = (
            <GradeDisplay
                emissions={display.emissions}
                storage={display.storage}
                emissionGrade={display.emissionGrade}
                storageGrade={display.storageGrade}
                customPadding={customPadding}
                customGap={customGap}
                borderHeight={borderHeight}
                layout="framingGroup"
            />
        );

        const percentageInner = (
            <PercentageDisplay
                emissions={display.emissions}
                storage={display.storage}
                customPadding={customPadding}
                customGap={customGap}
                borderHeight={borderHeight}
                layout="framingGroup"
            />
        );

        if (!showEditor) {
            return <div className={classes.framingGroupGradeReadonly}>{gradeInner}</div>;
        }

        return (
            <div className={classes.framingGroupGradeRoot}>
                <div
                    className={`${classes.framingGroupGradeCell} ${display.hasMismatch ? classes.mismatch : ""} ${
                        classes.clickable
                    }`}
                    {...expandCellHandlers}
                >
                    {showGrade ? gradeInner : percentageInner}
                </div>
                {expanded && (
                    <div
                        className={`${classes.editorPanel} ${classes.framingGroupPanel}`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {display.hasMismatch && (
                            <div className={classes.mismatchNotice}>
                                Framing inputs changed since this EPD data was saved. Review or reset.
                            </div>
                        )}
                        <div className={classes.editorRow}>
                            <div className={classes.editorRowInputs}>
                                <label className={classes.miniLabel}>
                                    Emissions (kgCO₂/m²)
                                    <input
                                        className={classes.miniInput}
                                        type="text"
                                        inputMode="decimal"
                                        value={draftEmissions}
                                        onChange={(ev) => setDraftEmissions(ev.target.value)}
                                    />
                                </label>
                                <label className={classes.miniLabel}>
                                    Storage (kgCO₂/m²)
                                    <input
                                        className={classes.miniInput}
                                        type="text"
                                        inputMode="decimal"
                                        value={draftStorage}
                                        onChange={(ev) => setDraftStorage(ev.target.value)}
                                    />
                                </label>
                                {isWood && (
                                    <label className={classes.miniLabel}>
                                        Wood region
                                        <select
                                            className={classes.woodProvinceSelect}
                                            value={draftWoodProvince}
                                            onChange={onWoodProvinceChange}
                                        >
                                            {WOOD_AVERAGE_REGION_KEYS.map((key) => (
                                                <option key={key} value={key}>
                                                    {key === DEFAULT_WOOD_PROVINCE_KEY ? `${key} (national avg.)` : key}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                )}
                                <button
                                    type="button"
                                    className={classes.resetBtn}
                                    title="Reset to defaults"
                                    onClick={resetEmbC}
                                >
                                    <span className={classes.resetBtnIcon} aria-hidden="true">
                                        ↺
                                    </span>
                                </button>
                            </div>
                            <button type="button" className={classes.applyBtn} onClick={applyEmbC}>
                                Apply
                            </button>
                        </div>
                        <div className={classes.sourceRow}>
                            <label className={classes.sourceLabel} htmlFor={sourceInputId}>
                                Source
                            </label>
                            <input
                                className={`${classes.sourceInput} keeper-ignore`}
                                type="text"
                                name="voltaEmbFramingCitation"
                                id={sourceInputId}
                                autoComplete="off"
                                autoCorrect="off"
                                autoCapitalize="off"
                                spellCheck={false}
                                data-lpignore="true"
                                data-1p-ignore="true"
                                data-bwignore="true"
                                data-form-type="other"
                                placeholder={
                                    embC
                                        ? USER_DEFINED_LABEL
                                        : isWood
                                          ? getWoodFramingDefaultSourceLabel(draftWoodProvince)
                                          : "—"
                                }
                                value={draftSource}
                                onChange={(ev) => setDraftSource(sanitizeSourceInput(ev.target.value))}
                            />
                        </div>
                    </div>
                )}
            </div>
        );
    }
);

export default EmbodiedCarbonFramingGroupGrade;
